import React, { useState, useEffect, useCallback } from "react";
import { UseFormReturn, FieldArrayWithId, FieldErrors } from "react-hook-form";
import {
  HiExclamationCircle,
  HiCheckCircle,
  HiClock,
  HiInformationCircle,
  HiRefresh,
} from "react-icons/hi";
import { HiTicket } from "react-icons/hi2";
import { StatusBadge } from "../StatusBadge";
import ScrollableContainer from "./ScrollableContainer";
import AttendeeForm from "./AttendeeForm";
import { CustomButton } from "../CustomButton";
import LanguageSelector from "../LanguageSelector";
import BackButton from "./BackButton";
import {
  TicketTypeWithId,
  AttendeeFormData,
  AdditionalAttendee,
  PurchaseStep,
  ValidationResult,
  ReservationResult,
} from "../../../hooks/useEventCardState";
import { ImExit } from "react-icons/im";

interface PurchaseViewProps {
  selectedTicket: TicketTypeWithId | undefined;
  ticketQuantity: number;
  userEmail?: string | null;
  form: UseFormReturn<AttendeeFormData>;
  fields: FieldArrayWithId<AttendeeFormData, "attendees", "id">[];
  watchedAttendees: AdditionalAttendee[];
  expandedAttendees: Set<number>;
  onToggleExpanded: (index: number) => void;
  onExpandAttendees: (indexes: number[]) => void;
  purchaseStep: PurchaseStep;
  validationResult: ValidationResult | null;
  reservationResult: ReservationResult | null;
  isProcessing: boolean;
  onBack: () => void;
  onLogout: () => void;
  onValidate: () => void;
  onReserve: () => void;
  onPay: () => void;
  onEditAttendees: () => void;
  onReservationExpired: () => void;
  onResendValidation: (email: string) => Promise<void>;
  resendingEmails: Set<string>;
  t: (key: string, options?: Record<string, unknown>) => string;
}

/** Countdown timer for reservation expiry */
const CountdownTimer: React.FC<{
  expiresAt: string;
  onExpire?: () => void;
  t: (key: string, options?: Record<string, unknown>) => string;
}> = ({ expiresAt, onExpire, t }) => {
  const [secondsLeft, setSecondsLeft] = useState(() => {
    const diff = Math.floor(
      (new Date(expiresAt).getTime() - Date.now()) / 1000
    );
    return Math.max(0, diff);
  });

  useEffect(() => {
    const interval = setInterval(() => {
      const diff = Math.floor(
        (new Date(expiresAt).getTime() - Date.now()) / 1000
      );
      const clamped = Math.max(0, diff);
      setSecondsLeft(clamped);
      if (clamped === 0) {
        clearInterval(interval);
        onExpire?.();
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt, onExpire]);

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const isUrgent = secondsLeft < 120;

  return (
    <div
      className={`flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${
        isUrgent
          ? "bg-red-500/20 border border-red-400/30 text-red-200"
          : "bg-amber-500/20 border border-amber-400/30 text-amber-200"
      }`}
    >
      <HiClock className="w-4 h-4 flex-shrink-0" />
      <span>
        {t("event.purchase.timeRemaining", {
          time: `${minutes}:${seconds.toString().padStart(2, "0")}`,
        })}
      </span>
    </div>
  );
};

/** Step indicator showing progress through the purchase flow */
const StepIndicator: React.FC<{
  currentStep: PurchaseStep;
  t: (key: string, options?: Record<string, unknown>) => string;
  isFree?: boolean;
}> = ({ currentStep, t, isFree }) => {
  const steps = [
    { key: "form", label: t("event.purchase.stepValidate") },
    { key: "validated", label: t("event.purchase.stepReserve") },
    {
      key: "reserved",
      label: isFree ? t("event.purchase.stepGet") : t("event.purchase.stepPay"),
    },
  ] as const;

  const stepIndex = steps.findIndex((s) => s.key === currentStep);

  return (
    <div className="flex items-center gap-1 w-full">
      {steps.map((step, i) => (
        <React.Fragment key={step.key}>
          <div className="flex items-center gap-1.5 min-w-0">
            <div
              className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                i < stepIndex
                  ? "bg-green-500 text-white"
                  : i === stepIndex
                  ? "bg-white text-gray-900"
                  : "bg-white/20 text-white/50"
              }`}
            >
              {i < stepIndex ? <HiCheckCircle className="w-4 h-4" /> : i + 1}
            </div>
            <span
              title={step.label}
              className={`text-xs font-medium truncate ${
                i === stepIndex ? "text-white" : "text-white/50"
              }`}
            >
              {step.label}
            </span>
          </div>
          {i < steps.length - 1 && (
            <div
              className={`flex-1 h-px min-w-[8px] ${
                i < stepIndex ? "bg-green-500" : "bg-white/20"
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

const PurchaseView: React.FC<PurchaseViewProps> = ({
  selectedTicket,
  ticketQuantity,
  userEmail,
  form,
  fields,
  watchedAttendees,
  expandedAttendees,
  onToggleExpanded,
  onExpandAttendees,
  purchaseStep,
  validationResult,
  reservationResult,
  isProcessing,
  onBack,
  onLogout,
  onValidate,
  onReserve,
  onPay,
  onEditAttendees,
  onReservationExpired,
  onResendValidation,
  resendingEmails,
  t,
}) => {
  // Check if any warning has not_validated
  const hasNotValidatedWarnings =
    validationResult?.warnings &&
    Object.values(validationResult.warnings).some((w) => "not_validated" in w);

  // A reservation keeps the price it was made at, whatever the ticket costs since.
  const isFree = reservationResult
    ? reservationResult.price_in_cents === 0
    : selectedTicket?.settings.price === 0;

  const handleSubmit = useCallback(() => {
    if (purchaseStep === "form") {
      onValidate();
    } else if (purchaseStep === "validated") {
      if (hasNotValidatedWarnings) {
        onValidate(); // Re-validate instead of reserve
      } else {
        onReserve();
      }
    } else if (purchaseStep === "reserved") {
      onPay();
    }
  }, [purchaseStep, onValidate, onReserve, onPay, hasNotValidatedWarnings]);

  // The sheets are folded by default: refused, a sheet must open for its errors to show.
  const openIncompleteAttendees = (errors: FieldErrors<AttendeeFormData>) => {
    const refused = Object.keys(errors.attendees ?? {})
      .map(Number)
      .filter(Number.isInteger);
    if (refused.length > 0) onExpandAttendees(refused);
  };

  const getButtonTitle = () => {
    if (isProcessing) return t("event.purchase.processing");

    switch (purchaseStep) {
      case "form":
        return t("event.purchase.validateAttendees");
      case "validated":
        if (hasNotValidatedWarnings) {
          return t("event.purchase.revalidateAttendees");
        }
        return t("event.purchase.reserveTickets");
      case "reserved":
        if (isFree) {
          return t("event.purchase.getFreeTickets");
        }
        return t("event.purchase.proceedToPayment");
      default:
        return t("event.purchase.validateAttendees");
    }
  };

  const isButtonDisabled =
    isProcessing ||
    (purchaseStep === "validated" &&
      !hasNotValidatedWarnings &&
      (validationResult?.effective_quantity === 0 ||
        validationResult?.can_proceed === false));

  return (
    <ScrollableContainer>
      <form
        onSubmit={form.handleSubmit(handleSubmit, openIncompleteAttendees)}
        className="flex flex-col gap-4"
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          {purchaseStep !== "reserved" && (
            <BackButton
              onClick={purchaseStep === "form" ? onBack : onEditAttendees}
              label={
                purchaseStep === "form"
                  ? t("event.purchase.backToTickets")
                  : t("event.purchase.backToEdit")
              }
            />
          )}
          {purchaseStep === "reserved" && <div />}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onLogout}
              className="flex items-center justify-center w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white hover:scale-105 transition-all duration-200"
            >
              <ImExit size={16} />
            </button>
            <LanguageSelector variant="compact" />
          </div>
        </div>

        {/* Step indicator */}
        <StepIndicator
          currentStep={purchaseStep}
          t={t}
          isFree={(reservationResult ?? validationResult)?.price_in_cents === 0}
        />

        {/* Ticket summary */}
        {selectedTicket && (
          <div className="rounded-xl bg-black/25 border border-white/20 p-4">
            <div className="flex items-start gap-3">
              <HiTicket className="text-white mt-1" size={24} />
              <div className="flex-1 min-w-0">
                <h2 className="font-semibold text-white truncate">
                  {selectedTicket.settings.name}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <p className="text-sm text-white/70">
                    {t("event.purchase.quantity", {
                      quantity:
                        reservationResult?.quantity_reserved ?? ticketQuantity,
                    })}
                  </p>
                  {isFree && (
                    <StatusBadge color="success" size="sm" emphasis="solid">
                      {t("event.tickets.free")}
                    </StatusBadge>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Countdown timer (only on reserved step) */}
        {purchaseStep === "reserved" && reservationResult?.expires_at && (
          <CountdownTimer
            expiresAt={reservationResult.expires_at}
            onExpire={onReservationExpired}
            t={t}
          />
        )}

        {/* STEP: form - show attendee form */}
        {purchaseStep === "form" && (
          <>
            {/* Primary Attendee */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                {t("event.purchase.primaryAttendee")}
              </label>
              <div className="bg-green-500/20 border border-green-400/30 rounded-xl p-3 backdrop-blur">
                <p className="text-sm text-white">
                  <span className="font-semibold">
                    {t("event.purchase.email")}
                  </span>{" "}
                  {userEmail}
                </p>
                <p className="text-xs text-green-300 mt-1">
                  {t("event.purchase.autoRegistered")}
                </p>
              </div>
            </div>

            {/* Additional Attendees */}
            <AttendeeForm
              fields={fields}
              control={form.control}
              errors={form.formState.errors}
              watchedAttendees={watchedAttendees}
              expandedAttendees={expandedAttendees}
              onToggleExpanded={onToggleExpanded}
              isProcessing={isProcessing}
              isCalculated={false}
              onAttendeeEmailChange={() => {}}
              t={t}
            />
          </>
        )}

        {/* STEP: validated - show validation summary */}
        {purchaseStep === "validated" && validationResult && (
          <div className="flex flex-col gap-3">
            {/* Validation success */}
            <div className="rounded-xl bg-green-500/15 border border-green-400/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <HiCheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-sm font-medium text-green-300">
                  {t("event.purchase.validationPassed")}
                </span>
              </div>
              <p className="text-xs text-white/70">
                {t("event.purchase.effectiveQuantity", {
                  quantity: validationResult.effective_quantity,
                })}
              </p>
              {validationResult.price_in_cents > 0 && (
                <p className="text-lg font-bold text-white mt-2">
                  {t("event.purchase.total", {
                    amount: validationResult.price_in_cents / 100,
                    currency: validationResult.currency,
                  })}
                </p>
              )}
              {validationResult.price_in_cents === 0 && (
                <p className="text-sm font-medium text-green-300 mt-1">
                  {t("event.tickets.free")}
                </p>
              )}
            </div>

            {/* Purchaser status */}
            {validationResult.purchaser_already_has_ticket && (
              <div className="flex items-center gap-2 rounded-lg bg-amber-500/15 border border-amber-400/30 px-3 py-2">
                <HiExclamationCircle className="w-4 h-4 text-amber-400 flex-shrink-0" />
                <span className="text-xs text-amber-200">
                  {t("event.purchase.youAlreadyHaveTicket")}
                </span>
              </div>
            )}

            {/* Warnings */}
            {validationResult.warnings &&
              Object.keys(validationResult.warnings).length > 0 && (
                <div className="rounded-xl bg-amber-500/15 border border-amber-400/30 p-3 space-y-2">
                  {Object.entries(validationResult.warnings).map(
                    ([email, warning]) => {
                      const warningKey = Object.keys(warning)[0];
                      const translationKey = `event.purchase.validationErrors.${warningKey}`;
                      const warningMessage = t(translationKey);

                      return (
                        <div
                          key={email}
                          className="flex flex-col items-center gap-2 text-xs text-amber-200"
                        >
                          <div className="flex items-center gap-2 text-xs text-amber-200">
                            <HiExclamationCircle className="w-3 h-3 text-amber-400 flex-shrink-0" />
                            <span className="flex-1">
                              {email}: {warningMessage}
                            </span>
                          </div>
                          {"not_validated" in warning && (
                            <button
                              type="button"
                              onClick={() => onResendValidation(email)}
                              disabled={resendingEmails.has(email)}
                              className="flex items-center gap-1 px-2 py-1 rounded-md bg-amber-500/20 border border-amber-400/30 text-amber-200 hover:bg-amber-500/30 transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0"
                            >
                              <HiRefresh
                                className={`w-3 h-3 ${
                                  resendingEmails.has(email)
                                    ? "animate-spin"
                                    : ""
                                }`}
                              />
                              <span>
                                {resendingEmails.has(email)
                                  ? t("event.purchase.sending")
                                  : t("event.purchase.resendValidation")}
                              </span>
                            </button>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>
              )}

            {/* Info messages */}
            {validationResult.info &&
              Object.keys(validationResult.info).length > 0 && (
                <div className="rounded-xl bg-blue-500/15 border border-blue-400/30 p-3 space-y-1">
                  {Object.entries(validationResult.info).map(
                    ([email, info]) => {
                      const infoKey = Object.keys(info)[0];
                      const translationKey = `event.purchase.validationInfo.${infoKey}`;
                      const infoMessage = t(translationKey);

                      return (
                        <div
                          key={email}
                          className="flex items-center gap-2 text-xs text-blue-200"
                        >
                          <HiInformationCircle className="w-3 h-3 text-blue-400 flex-shrink-0" />
                          <span>
                            {email} {infoMessage}
                          </span>
                        </div>
                      );
                    }
                  )}
                </div>
              )}

            {/* Valid attendees list */}
            {validationResult.valid_attendees.length > 0 && (
              <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-white/50 mb-2">
                  {t("event.purchase.validAttendees")}
                </p>
                <div className="space-y-1">
                  {validationResult.valid_attendees.map((attendee) => (
                    <div
                      key={attendee.user_email}
                      className="flex items-center gap-2 text-xs text-white/80"
                    >
                      <HiCheckCircle className="w-3 h-3 text-green-400 flex-shrink-0" />
                      <span>
                        {attendee.first_name} {attendee.last_name} (
                        {attendee.user_email})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STEP: reserved - show reservation confirmation */}
        {purchaseStep === "reserved" && reservationResult && (
          <div className="flex flex-col gap-3">
            <div className="rounded-xl bg-green-500/15 border border-green-400/30 p-3">
              <div className="flex items-center gap-2 mb-2">
                <HiCheckCircle className="w-4 h-4 text-green-400" />
                <span className="text-sm font-medium text-green-300">
                  {t("event.purchase.ticketsReserved")}
                </span>
              </div>
              <p className="text-xs text-white/70">
                {t("event.purchase.reservedQuantity", {
                  quantity: reservationResult.quantity_reserved,
                })}
              </p>
              {reservationResult.price_in_cents > 0 && (
                <p className="text-lg font-bold text-white mt-2">
                  {t("event.purchase.total", {
                    amount: reservationResult.price_in_cents / 100,
                    currency:
                      reservationResult.currency ?? validationResult?.currency,
                  })}
                </p>
              )}
            </div>

            <div className="rounded-xl bg-amber-500/10 border border-amber-400/20 p-3">
              <p className="text-xs text-amber-200/80">
                {t("event.purchase.completePaymentWarning")}
              </p>
            </div>
          </div>
        )}

        {/* Action button */}
        <CustomButton
          title={getButtonTitle()}
          type="submit"
          variant="primary"
          className={`w-full ${
            isButtonDisabled
              ? "!bg-gray-500 hover:!bg-gray-500"
              : purchaseStep === "reserved"
              ? "!bg-green-500 hover:!bg-green-600 !text-white"
              : "!bg-white hover:!bg-gray-100 !text-gray-900"
          } font-semibold shadow-lg`}
          disabled={isButtonDisabled}
        />
      </form>
    </ScrollableContainer>
  );
};

export default PurchaseView;
